import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Table, Spinner, Alert } from "react-bootstrap";
import EmployeeSearchAdmin from "../../common/empsearch/EmployeeSearchAdmin";
import "./ManagerStyles.css";
import { getUserTravelDetails } from "./api/TravelManagerApi";
import { useParams, useHistory } from 'react-router-dom';
import Dollars from '../../common/typeFormatting/Dollars';
import DateFmt from '../../common/typeFormatting/Date';

const calculateTimeOfService = (hireDate) => {
  const today = new Date();
  const hire = new Date(hireDate);
  
  let years = today.getFullYear() - hire.getFullYear();
  let months = today.getMonth() - hire.getMonth();
  
  if (months < 0 || (months === 0 && today.getDate() < hire.getDate())) {
    years--;
    months += 12;
  }
  
  if (today.getDate() < hire.getDate()) {
    months--;
    if (months < 0) {
      months = 11;
      years--;
    }
  }
  
  const yearsText = years === 1 ? "Year" : "Years";
  const monthsText = months === 1 ? "Month" : "Months";
  
  if (years === 0) {
    return `${months} ${monthsText}`;
  } else if (months === 0) {
    return `${years} ${yearsText}`;
  } else {
    return `${years} ${yearsText}, ${months} ${monthsText}`;
  }
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { 
    month: '2-digit',
    day: '2-digit',
    year: 'numeric'
  });
};

const TransactionRow = ({ transaction }) => {
  return (
    <tr className="transaction-row" style={{ backgroundColor: '#f8f9fa' }}>
      <td style={{ paddingLeft: '2rem', fontSize: 12 }}>
        <DateFmt>{transaction.tranDate}</DateFmt>
      </td>
      <td style={{ fontSize: 12 }}>
        {transaction.tranType}{transaction.description ? ` - ${transaction.description}` : ''}
      </td>
      <td></td>
      <td className="text-right" style={{ fontSize: 12, paddingRight: '2rem' }}>
        <Dollars>{transaction.amount}</Dollars>
      </td>
      <td colSpan={2}></td>
    </tr>
  );
};

const VoucherRow = ({ voucher, onToggleTransactions }) => {
  const [showTransactions, setShowTransactions] = useState(false);
  const currentYear = new Date().getFullYear();
  
  const isExpiringThisYear = (expirationDate) => {
    if (!expirationDate) return false;
    const expYear = new Date(expirationDate).getFullYear();
    return expYear === currentYear;
  };
  
  const toggleTransactions = () => {
    setShowTransactions(!showTransactions);
  };
  
  return (
    <>
      <tr 
        onClick={toggleTransactions} 
        style={{ 
          cursor: 'pointer',
          backgroundColor: isExpiringThisYear(voucher.expirationDate) ? '#fff3cd' : 'inherit'
        }}
      >
        <td style={{ width: '15%' }}><DateFmt>{voucher.issueDate}</DateFmt></td>
        <td style={{ width: '25%' }}>{voucher.voucherName}</td>
        <td style={{ width: '15%', textAlign: 'right' }}><Dollars>{voucher.initialAmount}</Dollars></td>
        <td style={{ width: '15%', textAlign: 'right', paddingRight: '2rem' }}><Dollars>{voucher.balance}</Dollars></td>
        <td style={{ width: '25%', paddingLeft: '4rem' }}>
          {voucher.expirationDate ? (
            <DateFmt>{voucher.expirationDate}</DateFmt>
          ) : (
            'Does not expire'
          )}
        </td>
        <td style={{ width: '5%', textAlign: 'center' }}>
          {voucher.transactions?.length > 0 ? (showTransactions ? '▲' : '▼') : ''}
        </td>
      </tr>
      
      {showTransactions && voucher.transactions?.map(transaction => (
        <TransactionRow key={transaction.tranId} transaction={transaction} />
      ))}
    </>
  );
};

const TravelManagerEmployeeDetails = () => {
  const { employeeId } = useParams();
  const history = useHistory();
  
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeDetails, setEmployeeDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeOfService, setTimeOfService] = useState('');
  const [travelVouchers, setTravelVouchers] = useState([]);
  const [partnerTechVouchers, setPartnerTechVouchers] = useState([]);
  const [travelTotals, setTravelTotals] = useState({ totalBalance: 0, totalBenefitReceived: 0 });
  const [partnerTechTotals, setPartnerTechTotals] = useState({ totalBalance: 0, totalBenefitReceived: 0 });

  // Initialize the selectedEmployee from employeeId in URL
  useEffect(() => {
    if (employeeId && !selectedEmployee) {
      setSelectedEmployee({
        employeeId: employeeId,
      });
    }
  }, [employeeId, selectedEmployee]);

  // Fetch employee details when the selectedEmployee changes
  useEffect(() => {
    const fetchData = async () => {
      try {
        // If no employee is selected, just set loading to false
        if (!selectedEmployee || !selectedEmployee.employeeId) {
          setLoading(false);
          return;
        }

        setLoading(true);
        const data = await getUserTravelDetails(selectedEmployee.employeeId);
        setEmployeeDetails(data);
        
        // Calculate time of service
        if (data.allowanceSeniorityDate) {
          setTimeOfService(calculateTimeOfService(data.allowanceSeniorityDate));
        }
        
        // Separate vouchers by allowance type
        const travel = data.vouchers.filter(v => v.allowanceTypeId === 'TRAVEL');
        const partnerTech = data.vouchers.filter(v => v.allowanceTypeId === 'PARTTECH');
        
        setTravelVouchers(travel);
        setPartnerTechVouchers(partnerTech);
        
        // Calculate totals for each type
        const travelBalance = travel.reduce((sum, v) => sum + v.balance, 0);
        const travelBenefit = travel.reduce((sum, v) => sum + v.initialAmount, 0);
        setTravelTotals({ totalBalance: travelBalance, totalBenefitReceived: travelBenefit });
        
        const techBalance = partnerTech.reduce((sum, v) => sum + v.balance, 0);
        const techBenefit = partnerTech.reduce((sum, v) => sum + v.initialAmount, 0);
        setPartnerTechTotals({ totalBalance: techBalance, totalBenefitReceived: techBenefit });
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching employee details:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedEmployee]);

  // Handle employee selection from search
  const handleEmployeeSelect = (employee) => {
    setSelectedEmployee(employee);
    history.push(`/travel/manager/employee/${employee.employeeId}`);
  };

  return (
    <Container fluid className="p-4">
      {/* Employee Search */}
      <Row className="mb-4">
        <Col md={12}>
          <Card>
            <Card.Body>
              <Card.Title>Employee Search</Card.Title>
              <Row>
                <Col>
                  <EmployeeSearchAdmin
                    onSelect={handleEmployeeSelect}
                    preselectedEmployee={selectedEmployee} />
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {loading ? (
        <div className="text-center p-5">
          <Spinner animation="border" />
          <p className="mt-3">Loading employee details...</p>
        </div>
      ) : error ? (
        <Alert variant="danger">Error: {error}</Alert>
      ) : !employeeDetails ? (
        <Card>
          <Card.Body className="text-center">
            <p>Please select an employee to view their allowance details.</p>
          </Card.Body>
        </Card>
      ) : (
        <>
          {/* Employee Header */}
          <Card className="mb-4">
            <Card.Body>
              <Card.Title>
                {employeeDetails.firstName} {employeeDetails.lastName} - Allowances
              </Card.Title>
            </Card.Body>
          </Card>
          
          {/* Travel Allowances Card */}
          <Card className="mb-4">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <Card.Title>Travel Allowances</Card.Title>
              </div>
              
              <div className="row mb-4">
                <div className="col-md-4 text-center">
                  <div className="text-muted mb-2">Years of Service at RBA</div>
                  <h4 className="mb-1">{timeOfService}</h4>
                  <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                    starting on {formatDate(employeeDetails.allowanceSeniorityDate)}
                  </div>
                </div>
                <div className="col-md-4 text-center">
                  <div className="text-muted mb-2">Total Benefit Received</div>
                  <h4><Dollars>{travelTotals.totalBenefitReceived}</Dollars></h4>
                </div>
                <div className="col-md-4 text-center">
                  <div className="text-muted mb-2">Available Balance</div>
                  <h4><Dollars>{travelTotals.totalBalance}</Dollars></h4>
                </div>
              </div>

              <Table hover responsive style={{ fontSize: 14 }}>
                <thead>
                  <tr>
                    <th style={{ width: '15%' }}>Issue Date</th>
                    <th style={{ width: '25%' }}>Description</th>
                    <th style={{ width: '15%', textAlign: 'right' }}>Initial Amount</th>
                    <th style={{ width: '15%', textAlign: 'right', paddingRight: '2rem' }}>Current Balance</th>
                    <th style={{ width: '25%', paddingLeft: '4rem' }}>Expiration Date</th>
                    <th style={{ width: '5%' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {travelVouchers.length > 0 ? (
                    travelVouchers.map(voucher => (
                      <VoucherRow key={voucher.voucherId} voucher={voucher} />
                    ))
                  ) : (
                    <tr><td colSpan="6">No travel allowances found.</td></tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          {/* Partner Tech Allowances Card */}
          {partnerTechVouchers.length > 0 && (
            <Card>
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <Card.Title>Partner Tech Allowances</Card.Title>
                </div>
                
                <div className="row mb-4">
                  <div className="col-md-4 text-center">
                    <div className="text-muted mb-2">Partner Level</div>
                    <h4 className="mb-1">{employeeDetails.partnerLevel || "N/A"}</h4>
                  </div>
                  <div className="col-md-4 text-center">
                    <div className="text-muted mb-2">Total Benefit Received</div>
                    <h4><Dollars>{partnerTechTotals.totalBenefitReceived}</Dollars></h4>
                  </div>
                  <div className="col-md-4 text-center">
                    <div className="text-muted mb-2">Available Balance</div>
                    <h4><Dollars>{partnerTechTotals.totalBalance}</Dollars></h4>
                  </div>
                </div>

                <Table hover responsive style={{ fontSize: 14 }}>
                  <thead>
                    <tr>
                      <th style={{ width: '15%' }}>Issue Date</th>
                      <th style={{ width: '25%' }}>Description</th>
                      <th style={{ width: '15%', textAlign: 'right' }}>Initial Amount</th>
                      <th style={{ width: '15%', textAlign: 'right', paddingRight: '2rem' }}>Current Balance</th>
                      <th style={{ width: '25%', paddingLeft: '4rem' }}>Expiration Date</th>
                      <th style={{ width: '5%' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {partnerTechVouchers.map(voucher => (
                      <VoucherRow key={voucher.voucherId} voucher={voucher} />
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          )}
        </>
      )}
    </Container>
  );
};

export default TravelManagerEmployeeDetails;